(() => {
  const script = document.getElementById('PromoPricingScript');
  if (!script) return;

  const AUTO_CODE = 'protein10';
  const AUTO_PERCENT = 10;
  const CACHE_KEY = 'fwn-active-percentage-discount';
  const currency = script.dataset.currency || 'EUR';
  const moneyFormat = script.dataset.moneyFormat || '';
  const labelTemplate = script.dataset.labelTemplate || 'Economisez [percent]% avec [code]';
  const formatter = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
    style: 'currency',
    currency,
  });

  let activeDiscount = null;
  let refreshPromise = null;
  let observer = null;

  const normalizeCode = (value) => String(value || '').trim().toLowerCase();
  const cents = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const money = (value) => {
    const safeValue = Math.max(Number(value) || 0, 0);
    if (!moneyFormat) return formatter.format(safeValue / 100);
    const placeholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
    };
    const [precision, thousands, decimal] = formats[placeholder] || formats.amount;
    const parts = (safeValue / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const formatted = parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  };
  const label = (percent, code) => labelTemplate
    .replaceAll('[percent]', String(percent))
    .replaceAll('[code]', String(code || '').toUpperCase());

  const readCache = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (value && Number(value.percent) > 0 && value.code) return value;
    } catch (_error) {
      // A blocked or stale sessionStorage entry must never break storefront prices.
    }
    return null;
  };

  const writeCache = (discount) => {
    try {
      if (discount) sessionStorage.setItem(CACHE_KEY, JSON.stringify(discount));
      else sessionStorage.removeItem(CACHE_KEY);
    } catch (_error) {
      // Storage is an optional speed-up; Shopify cart data remains authoritative.
    }
  };

  const currentDiscountFor = (element) => {
    if (activeDiscount) return activeDiscount;
    if (element.dataset.promoProtein === 'true') {
      return { code: AUTO_CODE, percent: AUTO_PERCENT, automatic: true };
    }
    return null;
  };

  const renderElement = (element) => {
    if (!(element instanceof HTMLElement)) return;
    // Reward products render their points cost server-side; leave that price untouched.
    if (element.closest('[data-fpo-points-only]')) return;
    const current = element.querySelector('[data-promo-current]');
    const compare = element.querySelector('[data-promo-compare]');
    const promotion = element.querySelector('[data-promo-label]');
    if (!current) return;

    const quantity = Math.max(cents(element.dataset.promoQuantity), 1);
    const basePrice = cents(element.dataset.basePrice) * quantity;
    const nativeComparePrice = cents(element.dataset.comparePrice) * quantity;
    const discount = currentDiscountFor(element);

    if (discount) {
      current.textContent = money(Math.round(basePrice * (100 - discount.percent) / 100));
      if (compare) {
        compare.textContent = money(Math.max(nativeComparePrice, basePrice));
        compare.hidden = false;
        compare.classList.remove('is-hidden');
      }
      if (promotion) {
        promotion.textContent = label(discount.percent, discount.code);
        promotion.classList.remove('is-hidden');
      }
      element.classList.add('is-promo-priced');
      element.classList.add('price--on-sale');
      return;
    }

    current.textContent = money(basePrice);
    const onSale = nativeComparePrice > basePrice;
    if (compare) {
      compare.textContent = onSale ? money(nativeComparePrice) : '';
      compare.hidden = !onSale;
      compare.classList.toggle('is-hidden', !onSale);
    }
    if (promotion) {
      promotion.textContent = onSale ? (element.dataset.promoDefaultLabel || '') : '';
      promotion.classList.toggle('is-hidden', !onSale || !promotion.textContent);
    }
    element.classList.remove('is-promo-priced');
    element.classList.toggle('price--on-sale', onSale);
  };

  const renderAll = (root = document) => {
    if (root instanceof HTMLElement && root.matches('[data-promo-price]')) renderElement(root);
    root.querySelectorAll?.('[data-promo-price]').forEach(renderElement);
  };

  const syncObserver = () => {
    if (activeDiscount && !observer) {
      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) renderAll(node);
        }));
      });
      const targets = [
        document.querySelector('main'),
        document.querySelector('[data-cart-drawer]'),
        document.getElementById('QuickAddDialog'),
      ].filter(Boolean);
      (targets.length ? targets : [document.body]).forEach((target) => {
        observer.observe(target, { childList: true, subtree: true });
      });
    } else if (!activeDiscount && observer) {
      observer.disconnect();
      observer = null;
    }
  };

  const percentageApplication = (application) => {
    const value = Number(application?.value || application?.value?.amount || 0);
    const type = String(application?.value_type || application?.valueType || '').toLowerCase();
    if (!Number.isFinite(value) || value <= 0 || (type && type !== 'percentage')) return null;
    const code = application.title || application.code;
    if (!code || normalizeCode(code) === AUTO_CODE) return null;
    return { code, percent: Math.round(value), automatic: false };
  };

  const collectCodeApplications = (cart) => {
    const applications = [];
    (cart?.cart_level_discount_applications || []).forEach((entry) => applications.push(entry));
    (cart?.discount_applications || []).forEach((entry) => applications.push(entry));
    (cart?.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        if (allocation?.discount_application) applications.push(allocation.discount_application);
      });
    });
    return applications.filter((entry) => entry && entry.type === 'discount_code');
  };

  const cartPercentageDiscount = (applications) => (
    applications.map(percentageApplication).find(Boolean) || null
  );

  const applyAutomaticCode = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: AUTO_CODE }),
    });
    if (!response.ok) return null;
    const cart = await response.json();
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'promo-pricing' } }));
    return cart;
  };

  const refresh = async () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        let cart = await response.json();
        let applications = collectCodeApplications(cart);
        activeDiscount = cartPercentageDiscount(applications);

        const appliedCodes = applications.map((entry) => normalizeCode(entry.title || entry.code));
        const needsProteinCode = cart.item_count > 0
          && document.querySelector('[data-cart-line][data-protein-discount="true"]')
          && !activeDiscount
          && !appliedCodes.includes(AUTO_CODE)
          && !appliedCodes.some((code) => code && code !== AUTO_CODE);
        if (needsProteinCode) {
          cart = await applyAutomaticCode() || cart;
          applications = collectCodeApplications(cart);
          activeDiscount = cartPercentageDiscount(applications);
        }

        writeCache(activeDiscount);
        renderAll();
        syncObserver();
      } catch (_error) {
        // Pricing remains server-rendered when the cart endpoint is unavailable.
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  };

  activeDiscount = readCache();
  renderAll();
  syncObserver();
  refresh();

  document.addEventListener('fwn:cart-rendered', refresh);
  window.addEventListener('pageshow', refresh);
  window.FWNPromoPricing = { refresh, renderAll, renderElement };
})();
